/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { getBvmSurveys, saveBvmSurvey, getBvmSurveyStatistics, getBvmCategoryData, getBvmSurveysAdmin, updateBvmCategory, createOrUpdateBvmQuestion, deleteBvmQuestion, exportBvmSurveys } from './bvmApi';
import { logoutUser } from '../user/userApi';
import { BvmState } from './types';

const initialState = {
    error: null,
    isFetching: false,
    isExporting: false,
    surveys: [],
    period: '',
    school_id: null,
    // Survey statistics
    surveyStatistics: null,
    surveySchools: [],
    selectedSchool: null,
    statisticsDate: null,
    // Category data
    singleCategory: null,
    categoryPeriod: null,
    categoryDate: null,
    categorySchoolId: null,
    // Admin categories
    categories: [],
} as BvmState;

export const bvmSlice = createSlice({
    name: 'bvm',
    initialState,
    reducers: {
        resetBvmState: (state) => {
            state.surveys = [];
            state.period = '';
            state.school_id = null;
            state.surveyStatistics = null;
            state.surveySchools = [];
            state.selectedSchool = null;
            state.statisticsDate = null;
            state.singleCategory = null;
            state.categoryPeriod = null;
            state.categoryDate = null;
            state.categorySchoolId = null;
            state.categories = [];
        },
        setSelectedSchool: (state, action) => {
            state.selectedSchool = action.payload;
        },
        clearSelectedSchool: (state) => {
            state.selectedSchool = null;
        },
        clearCategoryData: (state) => {
            state.singleCategory = null;
            state.categoryPeriod = null;
            state.categoryDate = null;
            state.categorySchoolId = null;
        },
    },
    extraReducers: (builder) => {
        // Get BVM surveys
        builder
            .addCase(getBvmSurveys.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(getBvmSurveys.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                state.surveys = action.payload.surveys;
                state.period = action.payload.period;
                state.school_id = action.payload.school_id;
            })
            .addCase(getBvmSurveys.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Save BVM survey
        builder
            .addCase(saveBvmSurvey.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(saveBvmSurvey.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                state.surveys = action.payload.surveys;
                state.period = action.payload.period;
                state.school_id = action.payload.school_id;
                // Optionally refresh surveys after save
                notification.success({
                    message: 'Pavyko!',
                    description: 'Apklausa sėkmingai išsaugota',
                });
            })
            .addCase(saveBvmSurvey.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Get BVM survey statistics
        builder
            .addCase(getBvmSurveyStatistics.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(getBvmSurveyStatistics.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                state.surveyStatistics = action.payload.statistics;
                state.surveySchools = action.payload.schools;
                state.statisticsDate = action.payload.date;
                state.period = action.payload.period;
            })
            .addCase(getBvmSurveyStatistics.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Get BVM category data
        builder
            .addCase(getBvmCategoryData.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(getBvmCategoryData.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                state.singleCategory = action.payload.category;
                state.categoryPeriod = action.payload.period;
                state.categoryDate = action.payload.date;
                state.categorySchoolId = action.payload.school_id;
            })
            .addCase(getBvmCategoryData.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Get BVM surveys admin
        builder
            .addCase(getBvmSurveysAdmin.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(getBvmSurveysAdmin.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                state.categories = action.payload.categories;
            })
            .addCase(getBvmSurveysAdmin.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Update BVM category
        builder
            .addCase(updateBvmCategory.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(updateBvmCategory.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                // Update the category in the categories array
                const updatedCategory = action.payload.category;
                const categoryIndex = state.categories.findIndex(cat => cat.id === updatedCategory.id);
                if (categoryIndex !== -1) {
                    const category = state.categories[categoryIndex];
                    if (category) {
                        category.title = updatedCategory.title;
                        category.description = updatedCategory.description;
                    }
                }
                notification.success({
                    message: 'Pavyko!',
                    description: action.payload.message,
                });
            })
            .addCase(updateBvmCategory.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Create or Update BVM question
        builder
            .addCase(createOrUpdateBvmQuestion.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(createOrUpdateBvmQuestion.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                const updatedQuestion = action.payload.question;
                const { category_id, id } = action.meta.arg as { category_id: number; id?: number };
                const categoryIndex = state.categories.findIndex(cat => cat.id === category_id);
                if (categoryIndex !== -1) {
                    const category = state.categories[categoryIndex];
                    if (category) {
                        const questions = (category.questions || []).slice();
                        if (id) {
                            const qIdx = questions.findIndex(q => q.id === id);
                            if (qIdx !== -1) {
                                questions[qIdx] = updatedQuestion;
                            } else {
                                questions.push(updatedQuestion);
                            }
                        } else {
                            questions.push(updatedQuestion);
                        }
                        // Pakeisti visą kategoriją, kad React pamatytų pokytį
                        state.categories[categoryIndex] = {
                            ...category,
                            questions,
                        };
                    }
                }
                notification.success({
                    message: 'Pavyko!',
                    description: action.payload.message,
                });
            })
            .addCase(createOrUpdateBvmQuestion.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Delete BVM question
        builder
            .addCase(deleteBvmQuestion.pending, (state: BvmState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(deleteBvmQuestion.fulfilled, (state: BvmState, action) => {
                state.isFetching = false;
                const { category_id, questions } = action.payload;
                const categoryIndex = state.categories.findIndex(cat => cat.id === category_id);
                if (categoryIndex !== -1) {
                    const category = state.categories[categoryIndex];
                    if (category) {
                        // Update the category with the new questions list from the API response
                        state.categories[categoryIndex] = {
                            ...category,
                            questions,
                        };
                    }
                }
                notification.success({
                    message: 'Pavyko!',
                    description: action.payload.message,
                });
            })
            .addCase(deleteBvmQuestion.rejected, (state: BvmState, action) => {
                state.isFetching = false;
                state.error = action.error;
            });

        // Export BVM surveys
        builder
            .addCase(exportBvmSurveys.pending, (state: BvmState) => {
                state.isExporting = true;
                state.error = null;
            })
            .addCase(exportBvmSurveys.fulfilled, (state: BvmState) => {
                state.isExporting = false;
            })
            .addCase(exportBvmSurveys.rejected, (state: BvmState, action) => {
                state.isExporting = false;
                state.error = action.error;
            });

        // on logout
        builder
            .addCase(logoutUser.fulfilled, (state: BvmState) => {
                state.surveys = [];
                state.period = '';
                state.school_id = null;
                state.surveyStatistics = null;
                state.surveySchools = [];
                state.selectedSchool = null;
                state.statisticsDate = null;
                state.singleCategory = null;
                state.categoryPeriod = null;
                state.categoryDate = null;
                state.categorySchoolId = null;
                state.categories = [];
            });
    },
});

export const { resetBvmState, setSelectedSchool, clearSelectedSchool, clearCategoryData } = bvmSlice.actions;
export default bvmSlice.reducer;
